import * as Api from '@ViewModels';
import { UseQueryResult } from '@tanstack/react-query';
import { css } from 'aphrodite';
import * as React from 'react';
import { Noop } from '../../../../../../extViewmodels/Utils';
import { IContentCalendarSelectedSuggestion } from '../../../../../../models';
import {
	convertRawRichTextContentStateToRichContentEditorState,
	getSocialMediaPlainTextContentFromRawRichContent,
	getSocialMediaPlatformLogoUrl,
	getUniqueIdForSuggestion,
} from '../../../../../../models/UiUtils';
import { ImpersonationContextViewModel } from '../../../../../../viewmodels/AdminViewModels';
import { Button } from '../../../../../components-v2/Button';
import { UnifiedImageDialog } from '../../../../../components-v2/UnifiedImageDialog';
import { TabValue } from '../../../../../components-v2/UnifiedImageDialog/types';
import { Checkbox } from '../../../../../components/Checkbox';
import { ISelectOption, MultiSelect } from '../../../../../components/DeprecatedSelect';
import { ImagePreview } from '../../../../../components/ImagePreview';
import { VideoPreview } from '../../../../../components/ImagePreview/videoPreview';
import { ImageUploadButtons } from '../../../../../components/ImageUploadButtons';
import { LoadingSpinner } from '../../../../../components/LoadingSpinner';
import { RichContentDocumentEditor } from '../../../../../components/richContent/RichContentDocumentEditor';
import { PreviewSocialMediaPost } from '../../../../../components/socialMedia/PreviewSocialMediaPost';
import { SliderArrowRight } from '../../../../../components/svgs/icons/SliderArrowRight';
import { brandPrimary } from '../../../../../styles/colors';
import { bs } from '../../../../../styles/styles';
import { GeneratingContentLoader } from '../../emails/MultiEmailTemplateItem/generatingContentLoader';
import { styleSheet } from './styles';

const TARGETS = [
	{ provider: Api.SocialMediaType.Facebook },
	{ provider: Api.SocialMediaType.Instagram },
	{ provider: Api.SocialMediaType.LinkedIn },
];

export function SocialMediaPostPreviewSelector({
	templateQueries,
	selectedSuggestions = [],
	generatedContent = {},
	editedContent = {},
	editedImages = {},
	editedVideoJobs: _editedVideoJobs = {},
	overlayLogo,
	impersonationCtx,
	hideWhiteBackground,
	logoSizes = {},
	onLogoSizeChange,
	onPreviewChange,
}: {
	templateQueries?: UseQueryResult<Api.ITemplate, unknown>[];
	selectedSuggestions?: IContentCalendarSelectedSuggestion[];
	generatedContent?: Record<string, Api.IRawRichTextContentState>;
	editedContent?: Record<string, Api.IRawRichTextContentState>;
	editedImages?: Record<string, Api.IFileAttachment[]>;
	editedVideoJobs?: Record<string, string>;
	overlayLogo: boolean;
	impersonationCtx: Api.IImpersonationContext;
	hideWhiteBackground: boolean;
	logoSizes?: Record<string, number>;
	onLogoSizeChange?: (suggestionId: string, size: number) => void;
	onPreviewChange?: (suggestionId: string | null) => void;
}) {
	const [selectedTemplateIndex, setSelectedTemplateIndex] = React.useState<number>(0);
	const selectedTemplate = templateQueries[selectedTemplateIndex]?.data;
	const selectedSuggestion = selectedSuggestions[selectedTemplateIndex];

	// Determine which content and images to display, prioritizing edited > generated > template defaults
	const displayContent = React.useMemo(() => {
		if (!selectedTemplate || !selectedSuggestion) {
			return selectedTemplate?.content;
		}

		const id = getUniqueIdForSuggestion(selectedSuggestion);
		// Priority: editedContent > generatedContent > template content
		return editedContent[id] || generatedContent[id] || selectedTemplate.content;
	}, [selectedTemplate, selectedSuggestion, editedContent, generatedContent]);

	const displayImages = React.useMemo(() => {
		if (!selectedTemplate || !selectedSuggestion) {
			return selectedTemplate?.attachments;
		}

		const id = getUniqueIdForSuggestion(selectedSuggestion);
		// Priority: editedImages > template attachments
		// Note: editedVideoJobs are handled separately and don't replace images in preview
		return editedImages[id] || selectedTemplate.attachments;
	}, [selectedTemplate, selectedSuggestion, editedImages]);

	const postImages = displayImages;
	const postContent = React.useMemo(() => {
		return getSocialMediaPlainTextContentFromRawRichContent(displayContent);
	}, [displayContent]);

	const selectedSuggestionId = selectedSuggestion ? getUniqueIdForSuggestion(selectedSuggestion) : null;
	const currentLogoSize = selectedSuggestionId ? logoSizes[selectedSuggestionId] : undefined;

	React.useEffect(() => {
		onPreviewChange?.(selectedSuggestionId);
	}, [selectedSuggestionId, onPreviewChange]);

	const handleLogoSizeChange = (size: number) => {
		if (selectedSuggestionId) {
			onLogoSizeChange?.(selectedSuggestionId, size);
		}
	};

	const nextTemplate = () => {
		if (selectedTemplateIndex < templateQueries.length - 1) {
			setSelectedTemplateIndex(selectedTemplateIndex + 1);
			return;
		}
		setSelectedTemplateIndex(0);
	};

	const prevTemplate = () => {
		if (selectedTemplateIndex > 0) {
			setSelectedTemplateIndex(selectedTemplateIndex - 1);
			return;
		}
		setSelectedTemplateIndex(templateQueries.length - 1);
	};

	return (
		<PreviewSocialMediaPost
			postTargets={TARGETS}
			overlayLogo={overlayLogo}
			postContent={postContent}
			postImages={postImages}
			impersonationCtx={impersonationCtx}
			hideWhiteBackground={hideWhiteBackground}
			logoSize={currentLogoSize}
			onLogoSizeChange={handleLogoSizeChange}
		>
			{templateQueries?.length > 1 ? (
				<>
					<menu className={css(bs.listNone, styleSheet.previewMenu)}>
						{templateQueries.map((templateQuery, i) => {
							if (!templateQuery.isSuccess) {
								return <LoadingSpinner key={i} type='small' />;
							}
							const template = templateQuery.data;
							return (
								<li key={i}>
									<button
										type='button'
										className={css(styleSheet.bulletPointButton)}
										onClick={() => setSelectedTemplateIndex(i)}
									>
										<span
											className={css(
												styleSheet.bulletPoint,
												i === selectedTemplateIndex ? styleSheet.bulletPointActive : undefined
											)}
											aria-hidden='true'
										>
											{' '}
										</span>
										{i}. {template.name}
									</button>
								</li>
							);
						})}
					</menu>

					<footer className={css(bs.flex, bs.flexRow, bs.flexBetween, styleSheet.sidebarFooter)}>
						<button type='button' className={css(styleSheet.arrows)} onClick={prevTemplate}>
							Previous Post
							<SliderArrowRight className={css(styleSheet.doubleArrowPrev)} fillColor={brandPrimary} />
						</button>
						<p>Navigate Post Previews</p>
						<button type='button' className={css(styleSheet.arrows)} onClick={nextTemplate}>
							Next Post
							<SliderArrowRight fillColor={brandPrimary} />
						</button>
					</footer>
				</>
			) : null}
		</PreviewSocialMediaPost>
	);
}

export const MultiCampaignSocialMediaConnection = ({
	options,
	selectedOptions,
	onSelectionChanged,
	provider,
	clearSelection,
}: {
	options: ISelectOption<Api.ISocialMediaConnection>[];
	selectedOptions: ISelectOption<Api.ISocialMediaConnection>[];
	onSelectionChanged: (option: ISelectOption<Api.ISocialMediaConnection>) => void;
	provider: Api.SocialMediaType;
	clearSelection?: () => void;
}) => {
	const selectedConnections = selectedOptions?.map(x => x.dataContext) ?? [];

	return (
		<div key={`${provider}-target`} className={css(bs.wFull)}>
			<div className={css(styleSheet.socialMediaDropdown)}>
				<figure className={css(bs.mr1)}>
					<img
						key={provider}
						className={css(bs.h4, bs.w4)}
						src={getSocialMediaPlatformLogoUrl(provider)}
						alt={`${Api.SocialMediaType[provider]} logo`}
					/>
				</figure>

				<MultiSelect
					onOptionClick={onSelectionChanged}
					triggerStyles={options?.length === 0 ? [styleSheet.dropdownDisabled] : []}
					disabled={options?.length === 0}
					options={options}
					selectedOptions={selectedOptions}
					renderOption={(option: ISelectOption<Api.ISocialMediaConnection>) => {
						const isSelected = selectedOptions.indexOf(option) > -1;
						return (
							<div
								className={css(
									styleSheet.selectOption,
									option.dataContext?.state !== Api.SocialMediaConnectionState.Connected
										? styleSheet.disconnectedAccount
										: null
								)}
								key={option.id}
							>
								<Checkbox
									checked={isSelected || false}
									id={`connection-option-checkbox-${option.id}`}
									onClick={_e => {
										_e.preventDefault();
										_e.stopPropagation();
										onSelectionChanged(option);
									}}
									onChange={_e => Noop()}
								/>
								<span className={css(bs.truncateText)}>
									@{option.text} - <small>{option.dataContext.postTargetDisplayName}</small>
								</span>
							</div>
						);
					}}
					selectedOptionsTitle={
						<span
							className={css(
								bs.truncateText,
								selectedConnections &&
									selectedConnections.find(connection => connection.state !== Api.SocialMediaConnectionState.Connected)
									? styleSheet.disconnectedAccount
									: undefined
							)}
						>
							{selectedConnections.length === 1 ? (
								<span>
									@{selectedConnections[0].userName} - <small>{selectedConnections[0].postTargetDisplayName}</small>
								</span>
							) : null}

							{selectedConnections.length > 1 ? <span>{selectedConnections.length} accounts selected</span> : null}

							{!selectedConnections.length ? 'Select Account' : null}
						</span>
					}
				/>
				{selectedConnections.length ? (
					<button type='button' className={css(styleSheet.clearButton)} onClick={clearSelection}>
						Clear
					</button>
				) : null}
			</div>
		</div>
	);
};

export function SocialMediaTemplateItem({
	template,
	isGeneratingContent,
	videoElement,
}: {
	template: Api.ITemplate;
	isGeneratingContent: boolean;
	videoElement?: HTMLVideoElement | null;
}) {
	const previewImageUrl = template?.attachments?.[0]?.url;

	return (
		<div className={css(bs.flex, bs.flexCol, bs.gap4)}>
			<div className={css(bs.flex)}>
				<figure
					className={css(
						bs.overflowHidden,
						bs.relative,
						styleSheet.itemImageWrap,
						!previewImageUrl ? styleSheet.noImagePlaceholder : undefined
					)}
				>
					{videoElement ? (
						<video src={videoElement.src} />
					) : template?.attachments?.[0]?.mimeType?.startsWith('video/') ? (
						<video src={previewImageUrl} />
					) : previewImageUrl ? (
						<img src={previewImageUrl} alt={`${template.name} image preview`} />
					) : (
						<figcaption className={css(bs.m0)}>No Image Added</figcaption>
					)}
				</figure>
				<div className={css(bs.wFull, bs.relative)}>
					<GeneratingContentLoader
						isGeneratingContent={isGeneratingContent}
						className={css(styleSheet.generatingContent)}
					/>

					<RichContentDocumentEditor
						readOnly={true}
						contentState={convertRawRichTextContentStateToRichContentEditorState(template.content)}
						styles={[bs.textField, bs.wFull, bs.h28, bs.overflowAuto, styleSheet.richContentEditor]}
					/>
				</div>
			</div>
		</div>
	);
}

export function EditableSocialMediaTemplateItem({
	template,
	authorId,
	onContentChange,
	onImageSelected,
	onSave,
	onCancel,
	onVideoSelected,
	impersonationContext,
	postContent,
}: {
	template: Api.ITemplate;
	authorId: string;
	onContentChange: (content: Api.IRawRichTextContentState) => void;
	onImageSelected: (image: Api.IFileAttachment | null) => void;
	onVideoSelected: (video: HTMLVideoElement | null, jobId: string) => void;
	onSave: () => void;
	onCancel: () => void;
	impersonationContext?: ImpersonationContextViewModel;
	postContent?: string;
}) {
	const videoElementRef = React.useRef<HTMLVideoElement | null>(null);
	const [unifiedDialogOpen, setUnifiedDialogOpen] = React.useState(false);
	const [unifiedDialogTab, setUnifiedDialogTab] = React.useState<'upload' | 'free-images' | 'ai-edit' | 'upload-video'>(
		'upload'
	);
	const [currentImage, setCurrentImage] = React.useState<Api.IFileAttachment | undefined>(template?.attachments?.[0]);

	// Sync currentImage with template.attachments when template changes
	React.useEffect(() => {
		setCurrentImage(template?.attachments?.[0]);
	}, [template?.attachments]);

	const previewImageUrl = currentImage?.url || template?.attachments?.[0]?.url;
	const contentState = React.useMemo(
		() => convertRawRichTextContentStateToRichContentEditorState(template.content),
		[template.content]
	);

	const handleContentStateChanged = React.useCallback(
		(content: Api.IRichContentEditorState) => {
			onContentChange(content.getRawRichTextContent());
		},
		[onContentChange]
	);

	const handleImageSelected = React.useCallback(
		(image: Api.IFileAttachment) => {
			setCurrentImage(image);
			onImageSelected(image);
		},
		[onImageSelected]
	);

	const disabledTabs = React.useMemo(() => {
		return !authorId ? (['upload-video'] as const) : [];
	}, [authorId]);

	const disabledTabsReason = React.useMemo(() => {
		return !authorId ? { 'upload-video': 'Please select an author to upload a video' } : {};
	}, [authorId]);

	const handleVideoJobGenerated = React.useCallback(
		(jobId: string, videoElement: HTMLVideoElement) => {
			videoElementRef.current = videoElement;
			setCurrentImage(undefined);
			onImageSelected(null);
			setUnifiedDialogOpen(false);
			onVideoSelected(videoElement, jobId);
		},
		[onImageSelected, onVideoSelected]
	);

	const handleOpenImageDialog = React.useCallback((tab: 'upload' | 'free-images' | 'ai-edit' | 'upload-video') => {
		setUnifiedDialogTab(tab);
		setUnifiedDialogOpen(true);
	}, []);

	return (
		<div className={css(bs.flex, bs.flexCol, bs.gap4)}>
			<div className={css(bs.flex, bs.flexCol, bs.gap4)}>
				{/* Content Editor */}
				<div className={css(bs.wFull, bs.relative)}>
					<RichContentDocumentEditor
						readOnly={false}
						config={{
							toolbar: false,
						}}
						contentState={contentState}
						onContentStateChanged={handleContentStateChanged}
						styles={[bs.textField, bs.wFull, bs.h28, bs.overflowAuto, styleSheet.richContentEditor]}
						impersonationContext={impersonationContext}
					/>
				</div>
			</div>

			{/* Image Section */}
			<div className={css(bs.flex, bs.flexCol, bs.gap2)}>
				{videoElementRef.current ? (
					<VideoPreview
						videoElement={videoElementRef.current}
						onRemove={() => {
							setCurrentImage(undefined);
							onImageSelected(null);
						}}
						onEdit={() => handleOpenImageDialog('upload-video')}
						editButtonLabel='Edit Media with AI'
					/>
				) : currentImage && previewImageUrl ? (
					<ImagePreview
						url={previewImageUrl}
						fileName={currentImage.fileName || 'image'}
						source={currentImage.uploadSource === Api.IAttachmentUploadSource.Pixabay ? 'Pixabay' : 'Upload'}
						fileSize={currentImage.fileSize || 0}
						onRemove={() => {
							setCurrentImage(undefined);
							onImageSelected(null);
						}}
						onEdit={() => handleOpenImageDialog('ai-edit')}
						editButtonLabel='Edit Media with AI'
					/>
				) : (
					<ImageUploadButtons
						hasImage={false}
						onClickFreeImage={() => handleOpenImageDialog('free-images')}
						onClickMedia={() => handleOpenImageDialog('upload')}
					/>
				)}
			</div>

			{/* Save/Cancel Buttons */}
			<div className={css(bs.flex, bs.gap2, bs.justifyEnd)}>
				<Button variant='secondary' onClick={onCancel}>
					Cancel
				</Button>
				<Button onClick={onSave}>Save</Button>
			</div>

			{/* Unified Image Dialog */}
			<UnifiedImageDialog
				open={unifiedDialogOpen}
				onOpenChange={setUnifiedDialogOpen}
				initialTab={unifiedDialogTab}
				initialImage={currentImage}
				onImageSelected={handleImageSelected}
				impersonationContext={impersonationContext}
				postContent={postContent}
				showUploadVideoTab={true}
				onVideoJobGenerated={handleVideoJobGenerated}
				disabledTabs={disabledTabs as TabValue[]}
				disabledTabsReason={disabledTabsReason}
			/>
		</div>
	);
}
